import { useEffect, useState } from "react";

import { UseMutationResult } from "@tanstack/react-query";

export type ActionStatusPhase = "pending" | "success" | "error";

interface UseActionStatusDialogResult {
  open: boolean;
  phase: ActionStatusPhase | null;
  close: () => void;
}

/**
 * Derives dialog open/phase state from a react-query mutation.
 *
 * - Opens as soon as the mutation leaves `idle` (covers `pending`, `success`,
 *   and `error` - react-query can sometimes batch `pending` -> `success`/`error`
 *   into a single render for fast mutations, so we can't rely on `isPending`
 *   alone ever being observed).
 * - Stays open through `success`/`error` so the outcome can be shown.
 * - `close()` is a no-op while the mutation is pending (can't dismiss mid-flight),
 *   and resets the mutation so the dialog fully closes and can be re-triggered.
 */
export function useActionStatusDialog<TData, TError, TVariables, TContext>(mutation: UseMutationResult<TData, TError, TVariables, TContext>): UseActionStatusDialogResult {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (mutation.status !== "idle") {
      setOpen(true);
    }
  }, [mutation.status]);

  function close() {
    if (mutation.isPending) return;
    setOpen(false);
    mutation.reset();
  }

  const phase: ActionStatusPhase | null = mutation.isPending ? "pending" : mutation.isSuccess ? "success" : mutation.isError ? "error" : null;

  return { open, phase, close };
}
